import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboarding } from './context/OnboardingContext';
import { YourNameStep } from './steps/YourNameStep';
import { YourRoleStep } from './steps/YourRoleStep';
import { DetailLevelStep } from './steps/DetailLevelStep';
import { TechnicalLevelStep } from './steps/TechnicalLevelStep';
import { ChooseWhatToDoFirstStep } from './steps/ChooseWhatToDoFirstStep';
import { Stepper } from './components/Stepper';
import { useUpdateOnboardingPreference } from '../../api/onboarding-preferences';

interface OnboardingFlowProps {
  onComplete?: () => void;
  onClose?: () => void;
  canSkipSteps?: boolean;
}

export const OnboardingFlowContent: React.FC<OnboardingFlowProps> = ({
  onComplete,
  onClose,
}) => {
  const { currentStep, goToNextStep, goToPreviousStep, canGoBack } =
    useOnboarding();

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC key to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        goToPreviousStep();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, goToPreviousStep]);

  const handleComplete = () => {
    onComplete?.();
  };

  const updateOnboardingPreference = useUpdateOnboardingPreference();

  const handleSkip = async () => {
    // Mark onboarding as skipped using preferences API
    try {
      await updateOnboardingPreference.mutateAsync({
        status: 'skipped',
      });
    } catch (error) {
      console.error('Failed to mark onboarding as skipped:', error);
      // Continue anyway - don't block navigation
    }

    // Navigate to new conversation page
    onClose?.();
    handleComplete();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'your-name':
        return <YourNameStep onNext={goToNextStep} />;
      case 'your-role':
        return <YourRoleStep onNext={goToNextStep} />;
      case 'detail-level':
        return <DetailLevelStep onNext={goToNextStep} />;
      case 'technical-level':
        return <TechnicalLevelStep onNext={goToNextStep} />;
      case 'choose-what-to-do-first':
        return <ChooseWhatToDoFirstStep onComplete={handleComplete} />;
      default:
        return null;
    }
  };

  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  const pageTransition = {
    type: 'tween' as const,
    ease: 'easeOut' as const,
    duration: 0.25, // Faster transitions
  };

  return (
    <motion.div
      className="fixed inset-0 z-[9999999999]"
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-gray-900/10"
        style={{ backdropFilter: 'blur(8px)' }}
        initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
        animate={{ opacity: 1, backdropFilter: 'blur(8px)' }}
        exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1],
        }}
      />

      {/* Container - Full width/height minus margin */}
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 10 }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30,
          mass: 0.8,
          delay: 0.1,
        }}
        className="absolute inset-4 flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-900"
      >
        <Stepper
          currentStep={currentStep}
          onSkip={
            currentStep !== 'choose-what-to-do-first' ? handleSkip : undefined
          }
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
};
